import { useEffect, useState } from 'react';
import { getReportInstructors, getInstructorPerformance } from '../../services/api';

// How one instructor's students are doing, against the academy as a whole.
//
// The client's example: "look, your students are smiling the least out of
// everybody". Every figure is shown next to the academy average, because a 71%
// pass rate means nothing until you know the academy runs at 77%.

const CATEGORY_LABELS = {
  smile: 'Smile',
  execution: 'Execution',
  energy: 'Energy',
  nakhra: 'Grace / Nakhra',
};

function Delta({ value, academy, suffix = '' }) {
  if (value == null || academy == null) return null;
  const diff = Math.round((value - academy) * 10) / 10;
  if (diff === 0) return <span className="text-xs text-gray-500">same as the academy</span>;
  const better = diff > 0;
  return (
    <span className={`text-xs font-medium ${better ? 'text-green-600' : 'text-red-600'}`}>
      {better ? '▲' : '▼'} {Math.abs(diff)}{suffix} vs academy {academy}{suffix}
    </span>
  );
}

function Kpi({ label, value, academy, suffix = '', empty, note }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</div>
      {value == null ? (
        <>
          <div className="mt-1 text-2xl font-semibold text-gray-300">—</div>
          <div className="text-xs text-gray-500">{empty}</div>
        </>
      ) : (
        <>
          <div className="mt-1 text-3xl font-semibold text-gray-900">{value}{suffix}</div>
          <Delta value={value} academy={academy} suffix={suffix} />
          {note && <div className="mt-1 text-xs text-gray-500">{note}</div>}
        </>
      )}
    </div>
  );
}

export default function InstructorPerformance() {
  const [instructors, setInstructors] = useState([]);
  const [selected, setSelected] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getReportInstructors()
      .then(({ data: result }) => {
        setInstructors(result.instructors || []);
        if (result.instructors?.length) setSelected(result.instructors[0].name);
      })
      .catch(() => setError('Could not load instructors'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return undefined;

    // Tapping through instructors quickly starts several requests; without this
    // guard a slow earlier one can land last and show the wrong instructor's
    // numbers under the selected name.
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const { data: result } = await getInstructorPerformance(selected);
        if (!cancelled) setData(result);
      } catch (loadError) {
        if (!cancelled) setError(loadError.response?.data?.error || 'Could not load this report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [selected]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!instructors.length && !loading) {
    return <p className="text-sm text-gray-500">No instructors are assigned to any class yet.</p>;
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex flex-wrap gap-2">
        {instructors.map((instructor) => (
          <button
            key={instructor.name}
            onClick={() => setSelected(instructor.name)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
              selected === instructor.name
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {instructor.name}
            <span className={selected === instructor.name ? 'text-gray-300' : 'text-gray-400'}>
              {' '}· {instructor.students}
            </span>
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {data && !loading && (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Kpi
              label="Ceremony pass rate"
              value={data.passRate.rate}
              academy={data.passRate.academy}
              suffix="%"
              empty="No ceremony has been scored yet"
              note={data.passRate.tested ? `${data.passRate.passed} of ${data.passRate.tested} passed` : null}
            />
            <Kpi
              label="Average score"
              value={data.averageScore.value}
              academy={data.averageScore.academy}
              empty="No ceremony has been scored yet"
              note="out of 40 · pass is 30"
            />
            <Kpi
              label="Class attendance"
              value={data.attendance.rate}
              academy={data.attendance.academy}
              suffix="%"
              empty="No attendance marked yet"
              note={`${data.attendance.sessions} sessions marked`}
            />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-1 text-sm font-semibold text-gray-900">Average per category</h3>
            <p className="mb-4 text-xs text-gray-500">
              {data.counted > 0
                ? `Across ${data.counted} scored ${data.counted === 1 ? 'result' : 'results'}. The black mark is the academy average.`
                : 'Fills in once a ceremony has been scored in the portal.'}
            </p>

            <div className="space-y-3">
              {data.categories.map((category) => (
                <div key={category.key}>
                  <div className="mb-1 flex items-baseline justify-between">
                    <span className={`text-sm ${category.key === data.weakest ? 'font-semibold text-red-600' : 'text-gray-700'}`}>
                      {CATEGORY_LABELS[category.key]}
                      {category.key === data.weakest && ' · lowest'}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {category.average == null ? '—' : category.average}
                    </span>
                  </div>
                  <div className="relative h-5 overflow-hidden rounded-md bg-gray-100">
                    {category.average != null && (
                      <div
                        className={`h-full rounded-md ${
                          category.key === data.weakest ? 'bg-red-400' : 'bg-purple-500'
                        }`}
                        style={{ width: `${(category.average / 10) * 100}%` }}
                      />
                    )}
                    {category.academy != null && (
                      <div
                        className="absolute top-0 h-full w-0.5 bg-gray-900"
                        style={{ left: `${(category.academy / 10) * 100}%` }}
                        title={`Academy average ${category.academy}`}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {data.weakest && data.counted > 0 && (
              <div className="mt-4 rounded-md border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900">
                <strong>{selected}</strong>’s students score lowest on{' '}
                <strong>{CATEGORY_LABELS[data.weakest]}</strong>. Worth a focus in class.
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Classes</h3>
            <div className="space-y-1.5">
              {data.classes.map((cls) => (
                <div key={cls.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{cls.label}</span>
                  <span className="text-gray-500">{cls.studentCount} students</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
